import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaExternalLinkAlt,
  FaRegCheckCircle,
  FaClock,
  FaGraduationCap,
  FaClipboardList,
  FaPassport,
  FaStethoscope,
  FaQuestionCircle,
  FaArrowRight,
  FaMapMarkerAlt,
  FaInfoCircle,
} from "react-icons/fa";


const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const STREAMS = [
  {
    key: "psw",
    title: "Post‑Study Work stream",
    blurb:
      "For recent graduates of eligible Australian higher education degrees.",
    chips: ["Full‑time work rights", "Length varies by qualification", "Onshore application"],
  },
  {
    key: "gw",
    title: "Graduate Work stream",
    blurb:
      "For graduates with eligible skills/qualifications linked to occupations in demand.",
    chips: ["Skills assessment may be needed", "Shorter stay typically", "Check list of eligible fields"],
  },
];

const FAQ = [
  {
    q: "How long can I stay on a 485?",
    a: "Length of stay depends on stream, your qualification level, and current policy settings. Always check the latest information before applying.",
  },
  {
    q: "Can I work full‑time on a 485?",
    a: "485 visas generally allow full‑time work. Conditions can vary, so confirm your specific grant notice and current rules.",
  },
  {
    q: "Do I need health insurance?",
    a: "You must maintain adequate health cover for the duration of your visa. Many applicants use OVHC suited to the 485 stage.",
  },
  {
    q: "When should I apply after finishing my course?",
    a: "Timing can be tight. We plan from completion/eligibility dates to lodge on time and keep your work rights pathway clear.",
  },
];

export default function PostStudyWork485() {
  useEffect(() => {
    document.title = "Post‑Study Work (Subclass 485) | Glide Education";
  }, []);

  // UI state
  const [activeStream, setActiveStream] = useState("psw");
  const [hasAusStudy, setHasAusStudy] = useState(false);
  const [hasEnglish, setHasEnglish] = useState(false);
  const [hasInsurance, setHasInsurance] = useState(false);
  const [hasSkillsAssess, setHasSkillsAssess] = useState(false);

  const ready = useMemo(() => {
    // Basic readiness heuristic; Graduate Work often needs skills assessment
    if (activeStream === "gw") return hasAusStudy && hasEnglish && hasInsurance && hasSkillsAssess;
    return hasAusStudy && hasEnglish && hasInsurance;
  }, [activeStream, hasAusStudy, hasEnglish, hasInsurance, hasSkillsAssess]);

  return (
    <motion.main
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="min-h-screen bg-white"
    >
      {/* HERO */}
      <div className="relative overflow-hidden">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/25 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-6xl px-6 pt-12 pb-8 md:pb-12">
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="text-3xl md:text-5xl font-extrabold tracking-tight text-blue-900"
          >
            Temporary Graduate Visa — Subclass 485
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="show"
            className="mt-3 max-w-3xl text-gray-700"
          >
            Work in Australia after graduation and gain practical experience. Stream selection, length of stay, and
            requirements depend on your qualification and current settings.
          </motion.p>

          <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-6 flex flex-wrap gap-3">
            <a
              href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/temporary-graduate-485"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-semibold text-white shadow hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Official Home Affairs page <FaExternalLinkAlt className="opacity-90" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-blue-600 bg-white px-5 py-2.5 font-semibold text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              Free Eligibility Check <FaArrowRight />
            </Link>
          </motion.div>

          {/* quick meta chips */}
          <motion.ul
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate="show"
            className="mt-6 flex flex-wrap gap-2 text-sm"
          >
            <li className="rounded-full bg-blue-50 px-3 py-1 text-blue-700 ring-1 ring-blue-200">Full‑time work rights</li>
            <li className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 ring-1 ring-indigo-200">Onshore transition</li>
            <li className="rounded-full bg-sky-50 px-3 py-1 text-sky-700 ring-1 ring-sky-200">Health cover required</li>
          </motion.ul>
        </div>
      </div>

      {/* BODY */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-6 pb-16 md:grid-cols-[1fr_320px]">
        {/* LEFT: content */}
        <div>
          {/* Streams */}
          <section id="streams" className="scroll-mt-24">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-2xl font-bold text-blue-800">
              Choose your stream
            </motion.h2>

            <div className="mt-4 flex flex-wrap gap-3">
              {STREAMS.map((s) => (
                <button
                  type="button"
                  key={s.key}
                  onClick={() => setActiveStream(s.key)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    activeStream === s.key
                      ? "bg-blue-600 text-white shadow"
                      : "bg-white text-blue-700 border border-blue-200 hover:bg-blue-50"
                  }`}
                  aria-pressed={activeStream === s.key}
                >
                  {s.title}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {STREAMS.filter((s) => s.key === activeStream).map((s) => (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                  className="mt-4 rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur"
                >
                  <div className="text-blue-900 font-semibold">{s.title}</div>
                  <p className="mt-1 text-gray-700 text-sm">{s.blurb}</p>
                  <ul className="mt-3 flex flex-wrap gap-2 text-xs">
                    {s.chips.map((c) => (
                      <li key={c} className="rounded-full bg-blue-50 px-3 py-1 text-blue-700 ring-1 ring-blue-200">
                        {c}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </AnimatePresence>
          </section>

          {/* Eligibility */}
          <section id="eligibility" className="mt-10 scroll-mt-24">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-2xl font-bold text-blue-800">
              Check your readiness
            </motion.h2>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <ChecklistItem
                id="chk-aus-study"
                checked={hasAusStudy}
                onChange={setHasAusStudy}
                icon={<FaGraduationCap />}
                label="I meet the Australian study requirement for my stream"
              />
              <ChecklistItem
                id="chk-english"
                checked={hasEnglish}
                onChange={setHasEnglish}
                icon={<FaClipboardList />}
                label="I meet the current English requirement"
              />
              <ChecklistItem
                id="chk-insurance"
                checked={hasInsurance}
                onChange={setHasInsurance}
                icon={<FaStethoscope />}
                label="I will maintain appropriate health cover (e.g., OVHC)"
              />
              {activeStream === "gw" && (
                <ChecklistItem
                  id="chk-skills"
                  checked={hasSkillsAssess}
                  onChange={setHasSkillsAssess}
                  icon={<FaPassport />}
                  label="I understand if a skills assessment is required for my occupation"
                />
              )}
            </div>

            <div
              className={`mt-4 rounded-lg px-4 py-3 text-sm ${
                ready ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-800"
              }`}
            >
              {ready ? (
                <span className="inline-flex items-center gap-2 font-medium">
                  <FaRegCheckCircle /> Looks good! You're broadly ready for the {activeStream === "psw" ? "Post‑Study Work" : "Graduate Work"} stream. We can confirm details and map lodgement timing.
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <FaInfoCircle className="text-blue-600" /> Not sure yet? We’ll review your course, timelines, and documentation to confirm the best pathway.
                </span>
              )}
            </div>
          </section>

          {/* Timeline */}
          <section id="timeline" className="mt-10 scroll-mt-24">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-2xl font-bold text-blue-800">
              Plan your 485 timeline
            </motion.h2>
            <ul className="mt-4 space-y-4">
              <TimelineItem
                icon={<FaClock />}
                title="Course completion → eligibility window"
                body="We anchor dates from your completion letter/results and align to the applicable lodgement window."
              />
              <TimelineItem
                icon={<FaClipboardList />}
                title="Collect documents"
                body="Evidence of study, English, health cover, biometrics/health checks if needed, and (for some) skills assessment."
              />
              <TimelineItem
                icon={<FaMapMarkerAlt />}
                title="Lodge onshore and transition"
                body="We aim for a smooth transition to maintain work rights and keep your longer‑term plan in view."
              />
            </ul>

            <Note>
              Policy settings change periodically. Always verify the current rules (streams, stay length, eligible courses) before applying.
            </Note>
          </section>

          {/* FAQ */}
          <section id="faq" className="mt-10 scroll-mt-24">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-2xl font-bold text-blue-800">
              FAQs
            </motion.h2>
            <div className="mt-3 space-y-3">
              {FAQ.map((f) => (
                <Accordion key={f.q} title={f.q} body={f.a} />
              ))}
            </div>
          </section>

          {/* CTA ribbon */}
          <section className="mt-12">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]"
            >
              <div className="rounded-2xl bg-white/90 px-6 py-8 backdrop-blur">
                <div className="grid items-center gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900">Ready to plan your 485?</h3>
                    <p className="mt-1 text-gray-600">
                      We’ll confirm the right stream, map timelines, and help assemble evidence so you can start work sooner.
                    </p>
                  </div>
                  <div className="flex md:justify-end">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500"
                    >
                      Talk to a Consultant <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>
        </div>

        {/* RIGHT: sticky quick nav */}
        <aside className="top-24 hidden md:block">
          <div className="sticky top-24 rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur">
            <div className="text-sm font-semibold text-blue-900">Quick links</div>
            <nav className="mt-3">
              <ul className="space-y-2 text-sm">
                <li><a className="text-blue-700 hover:underline" href="#streams">Choose your stream</a></li>
                <li><a className="text-blue-700 hover:underline" href="#eligibility">Check your readiness</a></li>
                <li><a className="text-blue-700 hover:underline" href="#timeline">Plan your timeline</a></li>
                <li><a className="text-blue-700 hover:underline" href="#faq">FAQs</a></li>
              </ul>
            </nav>
            <hr className="my-4 border-blue-100" />
            <a
              href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/temporary-graduate-485"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-800 hover:bg-blue-100"
            >
              Official guidance <FaExternalLinkAlt className="opacity-80" />
            </a>
          </div>
        </aside>
      </div>
    </motion.main>
  );
}

/* ---------- Reusable bits ---------- */
function Note({ children }) {
  return (
    <div className="my-4 rounded-md border-l-4 border-blue-600 bg-blue-50 p-4 text-sm">{children}</div>
  );
}

function ChecklistItem({ id, checked, onChange, icon, label }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-400"
      />
      <span className="flex-1 text-sm text-gray-800 flex items-start gap-2">
        <span className="mt-0.5 text-blue-600">{icon}</span>
        <span>{label}</span>
      </span>
    </label>
  );
}

function TimelineItem({ icon, title, body }) {
  return (
    <li className="rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur">
      <div className="flex items-center gap-3 text-blue-900">
        <span className="text-lg">{icon}</span>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="mt-1 text-sm text-gray-700">{body}</p>
    </li>
  );
}

function Accordion({ title, body }) {
  const [open, setOpen] = useState(false);
  const id = useMemo(() => `acc-${title.toLowerCase().replace(/\s+/g, "-")}`,[title]);

  return (
    <div className="rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg px-1"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        id={id}
      >
        <span className="font-medium text-blue-900 flex items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> {title}
        </span>
        <span className="text-blue-700" aria-hidden="true">{open ? "–" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${id}-panel`}
            role="region"
            aria-labelledby={id}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p className="mt-2 text-gray-700 text-sm leading-relaxed">{body}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}